import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import {
  DeleteMyFavoriteDocument,
  MyToplistsDocument,
  SetMyToplistSharingDocument,
  type FavoriteDomain,
  type FavoriteCoreFragment,
  type ToplistCoreFragment,
} from "@/graphql/generated/operations";
import { recipientIds, recipientRelations, sameRecipientIds } from "@/lib/content-sharing";
import { userErrorMessage } from "@/lib/user-error";
import type { PersonalShareSelection } from "./profile/PersonalSharingPicker";

// The own top 10 lists and who may read each one. Sharing edits and removals
// wait for the page's SaveBar like other profile edits
// (@adr SPARK-0005-unified-deferred-save-bar@1). Nothing is preselected: a list
// stays private until its owner shares it, because an own title can name a
// special category of personal data.

interface SharingEdit {
  sharedWith?: string[];
  allGroups?: boolean;
  publiclyReadable?: boolean;
}

// What the profile box needs for one list: the picker's state.
export interface ToplistSharingControl {
  selection: PersonalShareSelection;
  publication: { value: boolean; onChange: (value: boolean) => void };
}

export interface ToplistSharing {
  toplists: readonly ToplistCoreFragment[] | undefined;
  loading: boolean;
  error: unknown;
  control: (domain: FavoriteDomain) => ToplistSharingControl | undefined;
  dirty: boolean;
  remove: (favorite: FavoriteCoreFragment) => void;
  removalErrors: Readonly<Record<string, string>>;
  // Commits every changed list; rejects with the failures, keeping their edits.
  save: () => Promise<void>;
  discard: () => void;
}

export function useToplistSharing({
  skip,
  recipients,
}: {
  skip: boolean;
  // The groups a list may be shared with, from contentSharingRecipients.
  recipients: { list: readonly { id: string; name: string; eligible: boolean }[]; loading: boolean; error?: string };
}): ToplistSharing {
  const intl = useIntl();
  const query = useQuery(MyToplistsDocument, { skip });
  const [setSharing] = useMutation(SetMyToplistSharingDocument);
  const [deleteFavorite] = useMutation(DeleteMyFavoriteDocument);
  const [edits, setEdits] = useState<Partial<Record<FavoriteDomain, SharingEdit>>>({});
  const [removals, setRemovals] = useState<Record<string, FavoriteCoreFragment>>({});
  const [removalErrors, setRemovalErrors] = useState<Record<string, string>>({});
  const toplists = query.data?.myself?.toplists ?? (query.data ? [] : undefined);

  const patch = (domain: FavoriteDomain, change: SharingEdit) =>
    setEdits((previous) => ({ ...previous, [domain]: { ...previous[domain], ...change } }));

  const changed = (toplist: ToplistCoreFragment): SharingEdit | null => {
    const edit = edits[toplist.domain];
    if (!edit) return null;
    const differs =
      (edit.sharedWith !== undefined && !sameRecipientIds(edit.sharedWith, toplist.sharedWith)) ||
      (edit.allGroups !== undefined && edit.allGroups !== (toplist.allGroups === true)) ||
      (edit.publiclyReadable !== undefined && edit.publiclyReadable !== (toplist.publiclyReadable === true));
    return differs ? edit : null;
  };

  const control = (domain: FavoriteDomain): ToplistSharingControl | undefined => {
    const toplist = toplists?.find((candidate) => candidate.domain === domain);
    if (!toplist) return undefined;
    const edit = edits[domain] ?? {};
    const ids = edit.sharedWith ?? recipientIds(toplist.sharedWith);
    const allGroups = edit.allGroups ?? toplist.allGroups === true;
    const publiclyReadable = edit.publiclyReadable ?? toplist.publiclyReadable === true;
    // Groups the list is already shared with stay nameable after they stopped being offered.
    const known = new Map(recipients.list.map((group) => [group.id, group]));
    for (const group of toplist.sharedWith ?? []) {
      if (!known.has(group.id)) known.set(group.id, { id: group.id, name: group.name, eligible: group.eligible });
    }
    return {
      selection: {
        ids,
        recipients: [...known.values()],
        loading: recipients.loading,
        error: recipients.error,
        // The same three moves as a profile section's picker.
        allGroups: {
          value: allGroups,
          onChange: (value) => patch(domain, { allGroups: value, publiclyReadable: false, ...(value ? {} : { sharedWith: [] }) }),
        },
        onChange: (selected) => patch(domain, { allGroups: false, sharedWith: selected }),
      },
      publication: { value: publiclyReadable, onChange: (value) => patch(domain, { publiclyReadable: value, allGroups: false }) },
    };
  };

  const dirty = Object.keys(removals).length > 0 || (toplists ?? []).some((toplist) => changed(toplist) !== null);

  async function save() {
    const failures: string[] = [];
    // One after the other: each write touches the same person's sharing.
    for (const toplist of toplists ?? []) {
      const edit = changed(toplist);
      if (!edit) continue;
      try {
        await setSharing({
          variables: {
            input: {
              domain: toplist.domain,
              ...(edit.sharedWith !== undefined ? { sharedWith: recipientRelations(edit.sharedWith) } : {}),
              ...(edit.allGroups !== undefined ? { allGroups: edit.allGroups } : {}),
              ...(edit.publiclyReadable !== undefined ? { publiclyReadable: edit.publiclyReadable } : {}),
            },
          },
        });
        setEdits((previous) => {
          if (previous[toplist.domain] !== edit) return previous;
          const next = { ...previous };
          delete next[toplist.domain];
          return next;
        });
      } catch (err) {
        failures.push(userErrorMessage(intl, err));
      }
    }
    for (const favorite of Object.values(removals)) {
      try {
        await deleteFavorite({
          variables: { id: favorite.id },
          update: (cache) => {
            cache.evict({ id: cache.identify(favorite) });
            cache.gc();
          },
        });
        setRemovals((previous) => {
          const next = { ...previous };
          delete next[favorite.id];
          return next;
        });
        setRemovalErrors((previous) => {
          const next = { ...previous };
          delete next[favorite.id];
          return next;
        });
      } catch (err) {
        const message = userErrorMessage(intl, err);
        setRemovalErrors((previous) => ({ ...previous, [favorite.id]: message }));
        failures.push(message);
      }
    }
    if (failures.length > 0) throw new Error(failures.join(" "));
  }

  return {
    toplists: toplists?.map((toplist) => ({
      ...toplist,
      entries: toplist.entries.filter((entry) => !removals[entry.id] || removalErrors[entry.id]),
    })),
    loading: query.loading && !query.data,
    error: query.error,
    control,
    dirty,
    remove: (favorite) => {
      setRemovals((previous) => ({ ...previous, [favorite.id]: favorite }));
      setRemovalErrors((previous) => {
        const next = { ...previous };
        delete next[favorite.id];
        return next;
      });
    },
    removalErrors,
    save,
    discard: () => {
      setEdits({});
      setRemovals({});
      setRemovalErrors({});
    },
  };
}
